import { ReactNode, useMemo } from "react";

import useQuoteLocationMutation from "../../../data/useQuoteLocationMutation";
import useUtilities from "../../../data/useUtilities";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { ContactType } from "../../../models/Contact";
import { IQuoteLocation } from "../../../models/Lead";
import useNotification from "../../notifications/useNotifications";
import LocationForm, { LocationFormAddress, LocationFormValues } from "../../shared/Vehicle/LocationForm";

const emptyForm: LocationFormValues = {
  fullname: "",
  contactType: ContactType.Customer,
  telephone1: "",
  telephone2: "",
  email: "",
  line1: "",
  line2: null,
  line3: null,
  line4: null,
  locality: null,
  town: null,
  county: null,
  postcode: null,
};

function getFormValuesFromQuoteLocation(v: IQuoteLocation): LocationFormValues {
  return {
    fullname: v.fullname,
    contactType: v.contactType,
    telephone1: v.telephone1,
    telephone2: v.telephone2,
    email: v.email,
    line1: v.line1 ? v.line1.trim() : "",
    line2: v.line2,
    line3: v.line3,
    line4: v.line4,
    locality: v.locality,
    town: v.town,
    county: v.county,
    postcode: v.postcode,
  };
}

type AddressLookupResponse = {
  data: LocationFormAddress[];
};

interface QuoteLocationProps {
  leadId: string;
  quoteLocation: IQuoteLocation | null;
  children?: ReactNode;
  variant?: "embedded" | "standalone";
}

export default function QuoteLocation({
  leadId,
  quoteLocation,
  children = null,
  variant = "standalone",
}: QuoteLocationProps) {
  const { update } = useQuoteLocationMutation("QuoteLocation");
  const { getAddresses } = useUtilities();
  const { saveHandlers } = useDefaultCRUDHandlers("Quote location details");
  const { addNotification } = useNotification();
  const initialValues = useMemo(() => {
    return quoteLocation ? getFormValuesFromQuoteLocation(quoteLocation) : emptyForm;
  }, [quoteLocation]);

  const handleSubmit = (data: LocationFormValues) => {
    if (!quoteLocation) return;

    update.mutate(
      {
        leadId,
        quoteLocationId: quoteLocation.id,
        quoteLocation: data,
      },
      saveHandlers
    );
  };

  const handleGetAddresses = async (postcode: string) => {
    const response = await getAddresses.mutateAsync(postcode) as AddressLookupResponse;
    return response.data;
  };

  return (
    <LocationForm
      initialValues={initialValues}
      location={quoteLocation}
      onSubmit={handleSubmit}
      onGetAddresses={handleGetAddresses}
      onAddressLookupEmpty={() => {
        addNotification({
          variant: "error",
          primaryText: "No addresses found",
        });
      }}
      onAddressLookupError={() => {
        addNotification({
          variant: "error",
          primaryText: "Could not get addresses",
        });
      }}
      emptyMessage="No quote location is linked to this lead."
      heading="Quote location"
      title="Quote Location Details"
      variant={variant}
    >
      {children}
    </LocationForm>
  );
}
